import { useState } from 'react';
import { AlertTriangle, CalendarClock, CalendarDays, FileText, Flame, FlaskConical, Loader2, ShieldCheck, StickyNote, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/ui/section-header';
import { DatePicker } from '@/components/ui/date-picker';
import { form_error_message } from '@/lib/forms';
import {
  use_vendor_bookings,
  use_vendor_summary,
  use_booking_action,
  use_walker_settings,
  use_update_walker_settings,
} from '@/features/bookings/hooks';
import { use_draft } from '@/features/onboarding/hooks';
import type { Booking, BookingStatus } from '@/features/bookings/types';
import type { VendorBookingAction } from '@/features/bookings/api';
import { VisitRecordModal } from '@/components/visit-record-modal';
import { WalkReportModal } from '@/components/walk-report-modal';
import { TrainingReportModal } from '@/components/training-report-modal';
import { GroomReportModal } from '@/components/groom-report-modal';
import { WalkOtpModal } from '@/components/walk-otp-modal';
import { WalkIncidentsModal } from '@/components/walk-incidents-modal';
import { BookingDetailModal } from '@/components/booking-detail-modal';
import { CremationJourneyModal } from '@/components/cremation-journey-modal';
import { LabJourneyModal } from '@/components/lab-journey-modal';
import { RescheduleModal, NotesModal, ReasonModal } from '@/components/booking-ops-modals';
import { BookingSupportButton, MySupportRequests } from '@/components/booking-support-panel';
import { slot_label, slot_range_label } from '@/lib/datetime';

/** A clinical visit record can be authored for vet/clinic visits in progress or done. */
const CLINICAL_MODULES = ['VET', 'CLINIC'];
function can_record(b: Booking): boolean {
  return CLINICAL_MODULES.includes(b.module_type) && (b.status === 'IN_PROGRESS' || b.status === 'COMPLETED');
}
/** A walk report can be authored for WALKER walks in progress or done. */
function can_walk_report(b: Booking): boolean {
  return b.module_type === 'WALKER' && (b.status === 'IN_PROGRESS' || b.status === 'COMPLETED');
}
/** A session report can be authored for TRAINER sessions in progress or done. */
function can_training_report(b: Booking): boolean {
  return b.module_type === 'TRAINER' && (b.status === 'IN_PROGRESS' || b.status === 'COMPLETED');
}
/** A groom report (before/after) can be authored for GROOMER appointments in progress or done. */
function can_groom_report(b: Booking): boolean {
  return b.module_type === 'GROOMER' && (b.status === 'IN_PROGRESS' || b.status === 'COMPLETED');
}
function can_reschedule(b: Booking): boolean {
  return b.status === 'AWAITING_VENDOR' || b.status === 'CONFIRMED';
}
/** A cremation journey exists once the request is accepted (CONFIRMED onward). */
function can_cremation_journey(b: Booking): boolean {
  return b.module_type === 'CREMATION' && b.status !== 'AWAITING_VENDOR' && b.status !== 'PENDING_PAYMENT' && b.status !== 'CANCELLED';
}
/** A lab journey exists once the request is accepted (CONFIRMED onward). */
function can_lab_journey(b: Booking): boolean {
  return b.module_type === 'LAB' && b.status !== 'AWAITING_VENDOR' && b.status !== 'PENDING_PAYMENT' && b.status !== 'CANCELLED';
}

const FILTERS: { label: string; status?: BookingStatus }[] = [
  { label: 'Awaiting you', status: 'AWAITING_VENDOR' },
  { label: 'Confirmed', status: 'CONFIRMED' },
  { label: 'In progress', status: 'IN_PROGRESS' },
  { label: 'Completed', status: 'COMPLETED' },
  { label: 'Cancelled', status: 'CANCELLED' },
  { label: 'No-show', status: 'NO_SHOW' },
  { label: 'All', status: undefined },
];

const STATUS_STYLE: Record<BookingStatus, string> = {
  PENDING_PAYMENT: 'bg-muted text-muted-foreground',
  AWAITING_VENDOR: 'bg-warning/15 text-warning',
  CONFIRMED: 'bg-info/12 text-info',
  IN_PROGRESS: 'bg-primary/12 text-primary',
  COMPLETED: 'bg-success/12 text-success',
  CANCELLED: 'bg-destructive/12 text-destructive',
  NO_SHOW: 'bg-destructive/12 text-destructive',
};

const SUMMARY_CHIPS: BookingStatus[] = ['AWAITING_VENDOR', 'CONFIRMED', 'IN_PROGRESS', 'COMPLETED'];

function format_slot(iso: string): string {
  return slot_label(iso);
}

const ACTIONS_BY_STATUS: Partial<Record<BookingStatus, { action: VendorBookingAction; label: string; needs_reason?: boolean }[]>> = {
  AWAITING_VENDOR: [
    { action: 'accept', label: 'Accept' },
    { action: 'decline', label: 'Decline', needs_reason: true },
  ],
  CONFIRMED: [
    { action: 'start', label: 'Start' },
    { action: 'complete', label: 'Complete' },
    { action: 'no-show', label: 'No-show', needs_reason: true },
  ],
  IN_PROGRESS: [{ action: 'complete', label: 'Complete' }],
};

export function VendorBookingsPage() {
  const [filter_idx, set_filter_idx] = useState(0);
  const [date, set_date] = useState('');
  const status = FILTERS[filter_idx].status;
  const bookings_q = use_vendor_bookings(status, date || undefined);
  const summary_q = use_vendor_summary();
  const action_m = use_booking_action();

  const draft_q = use_draft();
  const is_walker = (draft_q.data?.module_types ?? []).includes('WALKER');
  const settings_q = use_walker_settings(is_walker);
  const otp_on = settings_q.data?.require_walk_otp ?? true; // default-on until known (matches backend)
  const update_settings_m = use_update_walker_settings();

  const [row_error, set_row_error] = useState<{ id: string; message: string } | null>(null);
  const [record_booking, set_record_booking] = useState<Booking | null>(null);
  const [walk_report_booking, set_walk_report_booking] = useState<Booking | null>(null);
  const [training_report_booking, set_training_report_booking] = useState<Booking | null>(null);
  const [groom_report_booking, set_groom_report_booking] = useState<Booking | null>(null);
  const [walk_otp, set_walk_otp] = useState<{ booking: Booking; kind: 'start' | 'end' } | null>(null);
  const [incidents_booking, set_incidents_booking] = useState<Booking | null>(null);
  const [journey_booking, set_journey_booking] = useState<Booking | null>(null);
  const [lab_journey_booking, set_lab_journey_booking] = useState<Booking | null>(null);
  const [finish_confirm, set_finish_confirm] = useState<Booking | null>(null);
  const [reschedule_booking, set_reschedule_booking] = useState<Booking | null>(null);
  const [notes_booking, set_notes_booking] = useState<Booking | null>(null);
  const [detail_booking_id, set_detail_booking_id] = useState<string | null>(null);
  const [reason_target, set_reason_target] = useState<{ booking: Booking; action: VendorBookingAction; label: string } | null>(null);

  const bookings = bookings_q.data ?? [];
  const summary = summary_q.data;

  /** The actual complete path (OTP end flow if enabled, else plain) — after any incident heads-up. */
  function complete_walk(booking: Booking) {
    set_finish_confirm(null);
    if (booking.module_type === 'WALKER' && otp_on) {
      set_walk_otp({ booking, kind: 'end' });
      return;
    }
    action_m.mutate({ id: booking.id, action: 'complete' }, {
      onError: (err) => set_row_error({ id: booking.id, message: form_error_message(err, 'Could not update this booking.') }),
    });
  }

  async function run(booking: Booking, action: VendorBookingAction, needs_reason?: boolean, label?: string) {
    set_row_error(null);
    // Finishing a walk with open incidents → heads-up confirm first (non-blocking).
    if (action === 'complete' && booking.module_type === 'WALKER' && (booking.open_incident_count ?? 0) > 0) {
      set_finish_confirm(booking);
      return;
    }
    // Walk start/complete are OTP-gated for walkers (unless the toggle is off).
    if (booking.module_type === 'WALKER' && otp_on && (action === 'start' || action === 'complete')) {
      set_walk_otp({ booking, kind: action === 'start' ? 'start' : 'end' });
      return;
    }
    if (needs_reason) {
      set_reason_target({ booking, action, label: label ?? action });
      return;
    }
    try {
      await action_m.mutateAsync({ id: booking.id, action });
    } catch (err) {
      set_row_error({ id: booking.id, message: form_error_message(err, 'Could not update this booking.') });
    }
  }

  async function submit_reason(reason: string) {
    if (!reason_target) return;
    const { booking, action } = reason_target;
    try {
      await action_m.mutateAsync({ id: booking.id, action, reason: reason || undefined });
      set_reason_target(null);
    } catch (err) {
      set_reason_target(null);
      set_row_error({ id: booking.id, message: form_error_message(err, 'Could not update this booking.') });
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader
        title="Bookings"
        description="Accept requests and track each booking through to completion."
      />

      {/* Walker: require an owner-delivered OTP to start/finish walks */}
      {is_walker && (
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid="walk-otp-setting">
            <div className="flex items-start gap-2">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div>
                <p className="text-sm font-semibold">Require walk OTP</p>
                <p className="text-xs text-muted-foreground">
                  Text the owner a code to start &amp; finish each walk. Turn off for one-tap start/finish.
                </p>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={otp_on}
              disabled={update_settings_m.isPending || settings_q.isLoading}
              onClick={() => update_settings_m.mutate(!otp_on)}
              data-testid="walk-otp-toggle"
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${otp_on ? 'bg-primary' : 'bg-muted'}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-card shadow transition-transform ${otp_on ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </CardContent>
        </Card>
      )}

      {/* Day-view summary */}
      {summary && (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="vendor-summary">
          <Card>
            <CardContent className="flex flex-wrap gap-2 p-4">
              {SUMMARY_CHIPS.map((s) => (
                <div key={s} className={`flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${STATUS_STYLE[s]}`}>
                  <span>{s.replace(/_/g, ' ').toLowerCase()}</span>
                  <span className="rounded-full bg-card px-1.5 text-foreground">{summary.counts[s] ?? 0}</span>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Today</p>
              {summary.today.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="summary-today-empty">Nothing scheduled today.</p>
              ) : (
                <ul className="flex flex-col gap-1" data-testid="summary-today">
                  {summary.today.slice(0, 4).map((b) => (
                    <li key={b.id} className="flex items-center justify-between text-sm">
                      <span>{format_slot(b.slot_start)}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLE[b.status]}`}>{b.status.replace(/_/g, ' ').toLowerCase()}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2" data-testid="bookings-filters">
        {FILTERS.map((f, i) => (
          <Button key={f.label} size="sm" variant={i === filter_idx ? 'gradient' : 'ghost'} onClick={() => set_filter_idx(i)} data-testid={`bookings-filter-${f.status ?? 'all'}`}>
            {f.label}
          </Button>
        ))}
        <div className="ml-auto flex items-center gap-1">
          <DatePicker value={date} onChange={set_date} className="w-44" placeholder="Filter by date" data-testid="bookings-date" />
          {date && (
            <Button size="sm" variant="ghost" onClick={() => set_date('')} aria-label="Clear date" data-testid="bookings-date-clear">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <MySupportRequests side="vendor" />

      <Card>
        <CardContent className="p-0">
          {bookings_q.isLoading ? (
            <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : bookings_q.error ? (
            <div className="p-6 text-sm text-destructive" data-testid="bookings-error">
              {form_error_message(bookings_q.error, 'Failed to load bookings.')}
            </div>
          ) : bookings.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-10 text-center text-sm text-muted-foreground" data-testid="bookings-empty">
              <CalendarDays className="h-6 w-6" />
              No bookings here yet.
            </div>
          ) : (
            <ul className="divide-y divide-border" data-testid="bookings-list">
              {bookings.map((b) => {
                // Cremation + lab status is driven by the fulfillment journey (accept only here).
                const base_actions =
                  (b.module_type === 'CREMATION' || b.module_type === 'LAB') && b.status !== 'AWAITING_VENDOR'
                    ? []
                    : ACTIONS_BY_STATUS[b.status] ?? [];
                // Plan/course sessions are accepted once at the plan/package level —
                // hide the per-day Accept (the rest of the lifecycle stays per session).
                const is_plan_child = Boolean(b.walk_plan_id || b.training_package_id);
                const actions = is_plan_child ? base_actions.filter((a) => a.action !== 'accept') : base_actions;
                return (
                  <li key={b.id} className="flex flex-col gap-2 p-4" data-testid={`booking-row-${b.id}`}>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold" data-testid={`booking-when-${b.id}`}>
                            {slot_range_label(b.slot_start, b.slot_end)}
                          </span>
                          <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium">{b.module_type}</span>
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLE[b.status]}`}>
                            {b.status.replace(/_/g, ' ').toLowerCase()}
                          </span>
                          {is_plan_child && b.status === 'AWAITING_VENDOR' && (
                            <span
                              className="rounded-full bg-info/12 px-2 py-0.5 text-[11px] font-medium text-info"
                              title={`Accept the whole ${b.walk_plan_id ? 'plan' : 'package'} from the ${b.walk_plan_id ? 'Walk plans' : 'Packages'} tab`}
                              data-testid={`booking-plan-child-${b.id}`}
                            >
                              part of {b.walk_plan_id ? 'plan' : 'package'}
                            </span>
                          )}
                          {b.module_type === 'WALKER' && b.start_otp_verified_at && (
                            <span className="flex items-center gap-1 rounded-full bg-success/12 px-2 py-0.5 text-[11px] font-medium text-success" data-testid={`walk-start-verified-${b.id}`}>
                              <ShieldCheck className="h-3 w-3" /> start
                            </span>
                          )}
                          {b.module_type === 'WALKER' && b.end_otp_bypassed ? (
                            <span className="flex items-center gap-1 rounded-full bg-warning/15 px-2 py-0.5 text-[11px] font-medium text-warning" data-testid={`walk-end-bypassed-${b.id}`}>
                              end bypassed
                            </span>
                          ) : b.module_type === 'WALKER' && b.end_otp_verified_at ? (
                            <span className="flex items-center gap-1 rounded-full bg-success/12 px-2 py-0.5 text-[11px] font-medium text-success" data-testid={`walk-end-verified-${b.id}`}>
                              <ShieldCheck className="h-3 w-3" /> end
                            </span>
                          ) : null}
                          {(b.open_incident_count ?? 0) > 0 && (
                            <span className="flex items-center gap-1 rounded-full bg-destructive/12 px-2 py-0.5 text-[11px] font-medium text-destructive" data-testid={`incident-chip-${b.id}`}>
                              <AlertTriangle className="h-3 w-3" /> {b.open_incident_count} open
                            </span>
                          )}
                        </div>
                        {b.notes && <p className="truncate text-xs text-muted-foreground">“{b.notes}”</p>}
                        {b.vendor_notes && <p className="flex items-center gap-1 truncate text-xs text-muted-foreground" data-testid={`vendor-note-${b.id}`}><StickyNote className="h-3 w-3 shrink-0" /> {b.vendor_notes}</p>}
                        {b.cancel_reason && (
                          <p className="text-xs text-muted-foreground">
                            {b.cancelled_by === 'USER' ? 'Customer' : b.cancelled_by === 'SYSTEM' ? 'System' : 'You'} cancelled: {b.cancel_reason}
                          </p>
                        )}
                      </div>
                      {/*
                        * What the vendor is actually paid, not just what was charged.
                        *
                        * On a service the platform's cut comes OUT of the customer's
                        * payment, so showing `price_inr` alone showed the one number that
                        * is not theirs. Take-home leads; the customer's price and the fee
                        * sit under it as the explanation.
                        */}
                      <div className="text-right text-sm tabular-nums" data-testid={`booking-money-${b.id}`}>
                        {b.payment_breakdown ? (
                          <>
                            <div className="font-heading font-bold text-primary" data-testid={`booking-payout-${b.id}`}>
                              ₹{b.payment_breakdown.vendor_payout_inr}
                            </div>
                            <div className="text-[11px] text-muted-foreground">you get</div>
                            <div className="mt-0.5 text-[11px] text-muted-foreground">
                              customer paid ₹{b.payment_breakdown.customer_price_inr}
                              {Number(b.payment_breakdown.platform_fee_inr) > 0 && (
                                <> · fee ₹{b.payment_breakdown.platform_fee_inr}</>
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="font-semibold">₹{b.price_inr}</div>
                        )}
                        {b.payment && (
                          <div className="text-xs text-muted-foreground">{b.payment.status === 'CAPTURED' ? 'Paid' : b.payment.status.toLowerCase()}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {actions.map((a) => (
                          <Button key={a.action} size="sm" variant={a.action === 'accept' ? 'gradient' : 'ghost'} disabled={action_m.isPending} onClick={() => run(b, a.action, a.needs_reason, a.label)} data-testid={`booking-${a.action}-${b.id}`}>
                            {a.label}
                          </Button>
                        ))}
                        {can_reschedule(b) && (
                          <Button size="sm" variant="ghost" onClick={() => set_reschedule_booking(b)} data-testid={`booking-reschedule-${b.id}`}>
                            <CalendarClock className="h-4 w-4" /> Reschedule
                          </Button>
                        )}
                        <BookingSupportButton side="vendor" booking={b} />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => set_detail_booking_id(b.id)}
                          data-testid={`booking-details-${b.id}`}
                        >
                          <CalendarClock className="h-4 w-4" /> Details
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => set_notes_booking(b)} aria-label="Private notes" data-testid={`booking-notes-${b.id}`}>
                          <StickyNote className="h-4 w-4" />
                        </Button>
                        {can_record(b) && (
                          <Button size="sm" variant="outline" onClick={() => set_record_booking(b)} data-testid={`booking-record-${b.id}`}>
                            <FileText className="h-4 w-4" /> Visit record
                          </Button>
                        )}
                        {can_walk_report(b) && (
                          <Button size="sm" variant="outline" onClick={() => set_walk_report_booking(b)} data-testid={`booking-walk-report-${b.id}`}>
                            <FileText className="h-4 w-4" /> Walk report
                          </Button>
                        )}
                        {can_training_report(b) && (
                          <Button size="sm" variant="outline" onClick={() => set_training_report_booking(b)} data-testid={`booking-training-report-${b.id}`}>
                            <FileText className="h-4 w-4" /> Session report
                          </Button>
                        )}
                        {can_groom_report(b) && (
                          <Button size="sm" variant="outline" onClick={() => set_groom_report_booking(b)} data-testid={`booking-groom-report-${b.id}`}>
                            <FileText className="h-4 w-4" /> Groom report
                          </Button>
                        )}
                        {b.module_type === 'WALKER' && (b.status === 'IN_PROGRESS' || (b.incidents?.length ?? 0) > 0) && (
                          <Button
                            size="sm"
                            variant={(b.open_incident_count ?? 0) > 0 ? 'outline' : 'ghost'}
                            onClick={() => set_incidents_booking(b)}
                            data-testid={`booking-incidents-${b.id}`}
                          >
                            <AlertTriangle className="h-4 w-4" />
                            {(b.incidents?.length ?? 0) > 0 ? `Incidents (${b.incidents!.length})` : 'Report incident'}
                          </Button>
                        )}
                        {/* Before the walk starts, incidents can't be raised yet — show a
                            disabled hint so it's clear why, rather than hiding it entirely. */}
                        {b.module_type === 'WALKER' && b.status === 'CONFIRMED' && (b.incidents?.length ?? 0) === 0 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled
                            data-testid={`booking-incidents-hint-${b.id}`}
                            title="Start the walk first — incidents can only be reported once it's in progress."
                          >
                            <AlertTriangle className="h-4 w-4" /> Report incident
                          </Button>
                        )}
                        {can_cremation_journey(b) && (
                          <Button size="sm" variant="outline" onClick={() => set_journey_booking(b)} data-testid={`booking-journey-${b.id}`}>
                            <Flame className="h-4 w-4" /> Journey
                          </Button>
                        )}
                        {can_lab_journey(b) && (
                          <Button size="sm" variant="outline" onClick={() => set_lab_journey_booking(b)} data-testid={`booking-lab-journey-${b.id}`}>
                            <FlaskConical className="h-4 w-4" /> Journey
                          </Button>
                        )}
                      </div>
                    </div>
                    {b.module_type === 'CREMATION' && (b.deceased_pet_name || b.pickup_address) && (
                      <div className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground" data-testid={`cremation-detail-${b.id}`}>
                        {b.deceased_pet_name && (
                          <p>
                            <span className="font-medium text-foreground">In memory of:</span> {b.deceased_pet_name}
                            {b.deceased_pet_species ? ` · ${b.deceased_pet_species.toLowerCase()}` : ''}
                            {b.deceased_pet_weight_kg ? ` · ${b.deceased_pet_weight_kg} kg` : ''}
                          </p>
                        )}
                        {b.pickup_address && (
                          <p>
                            <span className="font-medium text-foreground">Pickup:</span> {b.pickup_address}
                            {b.pickup_landmark ? `, ${b.pickup_landmark}` : ''}
                            {b.pickup_pincode ? ` — ${b.pickup_pincode}` : ''}
                          </p>
                        )}
                        <p>
                          <span className="font-medium text-foreground">Preferred pickup:</span> {format_slot(b.slot_start)}
                        </p>
                        {b.contact_phone && (
                          <p><span className="font-medium text-foreground">Contact:</span> {b.contact_phone}</p>
                        )}
                      </div>
                    )}
                    {(b.module_type === 'WALKER' || b.module_type === 'TRAINER' || b.module_type === 'LAB') && (b.pickup_address || b.contact_phone || b.access_notes) && (
                      <div className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground" data-testid={`walk-pickup-${b.id}`}>
                        {b.module_type === 'LAB' && (
                          <p className="mb-1 font-medium text-foreground">Home sample collection</p>
                        )}
                        {b.pickup_address && (
                          <p>
                            <span className="font-medium text-foreground">Pickup:</span> {b.pickup_address}
                            {b.pickup_landmark ? `, ${b.pickup_landmark}` : ''}
                            {b.pickup_pincode ? ` — ${b.pickup_pincode}` : ''}
                          </p>
                        )}
                        {b.contact_phone && (
                          <p><span className="font-medium text-foreground">Contact:</span> {b.contact_phone}</p>
                        )}
                        {b.access_notes && (
                          <p><span className="font-medium text-foreground">Access:</span> {b.access_notes}</p>
                        )}
                      </div>
                    )}
                    {row_error?.id === b.id && (
                      <p className="text-sm text-destructive" data-testid={`booking-error-${b.id}`}>{row_error.message}</p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {record_booking && <VisitRecordModal booking={record_booking} on_close={() => set_record_booking(null)} />}
      {walk_report_booking && <WalkReportModal booking={walk_report_booking} on_close={() => set_walk_report_booking(null)} />}
      {training_report_booking && <TrainingReportModal booking={training_report_booking} on_close={() => set_training_report_booking(null)} />}
      {groom_report_booking && <GroomReportModal booking={groom_report_booking} on_close={() => set_groom_report_booking(null)} />}
      {walk_otp && <WalkOtpModal booking={walk_otp.booking} kind={walk_otp.kind} on_close={() => set_walk_otp(null)} />}
      {incidents_booking && <WalkIncidentsModal booking={incidents_booking} on_close={() => set_incidents_booking(null)} />}
      {journey_booking && <CremationJourneyModal booking={journey_booking} on_close={() => set_journey_booking(null)} />}
      {lab_journey_booking && <LabJourneyModal booking={lab_journey_booking} on_close={() => set_lab_journey_booking(null)} />}
      {finish_confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" onClick={() => set_finish_confirm(null)}>
          <div className="w-full max-w-sm rounded-card border-2 border-brand-outline-variant/20 bg-card p-5 shadow-clay-lg" onClick={(e) => e.stopPropagation()} data-testid="finish-incident-confirm">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold">
              <AlertTriangle className="h-5 w-5 text-warning" /> Finish this walk?
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              This walk has {finish_confirm.open_incident_count} open incident{(finish_confirm.open_incident_count ?? 0) > 1 ? 's' : ''}. You can still resolve
              {(finish_confirm.open_incident_count ?? 0) > 1 ? ' them' : ' it'} after finishing.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => set_finish_confirm(null)}>Cancel</Button>
              <Button variant="gradient" onClick={() => complete_walk(finish_confirm)} data-testid="finish-incident-confirm-proceed">
                Finish anyway
              </Button>
            </div>
          </div>
        </div>
      )}
      {reschedule_booking && <RescheduleModal booking={reschedule_booking} on_close={() => set_reschedule_booking(null)} />}
      {notes_booking && <NotesModal booking={notes_booking} on_close={() => set_notes_booking(null)} />}
      {detail_booking_id && (
        <BookingDetailModal
          booking_id={detail_booking_id}
          side="vendor"
          on_close={() => set_detail_booking_id(null)}
        />
      )}
      {reason_target && (
        <ReasonModal
          title={`${reason_target.label} booking`}
          label="Reason"
          pending={action_m.isPending}
          on_submit={submit_reason}
          on_close={() => set_reason_target(null)}
        />
      )}
    </div>
  );
}
